import { useCallback, useRef, useState } from 'react';

// One form field's state: its value, the sentence it is refused with (or
// none: nothing before the first check, null when a check found nothing
// wrong; both are absence to every consumer), and a ref to focus it. The rule is a check from @psy/validations, the
// same one the services run.
//
// A field stays quiet until the person leaves it or submits the form; from
// then on it is checked on every change, so a message clears the moment the
// value is fixed rather than on the next blur. clean, when given, reshapes
// what is typed or pasted before it is kept, such as dropping the spaces in a
// pasted code.
export function useField({ initial, check, clean }) {
  // A profile field nobody has filled in yet arrives as null; to an input it
  // is simply empty.
  const [ value, setValue ] = useState(function() {
    if (initial == null) {
      return '';
    }

    return initial;
  });
  const [ error, setError ] = useState();
  const [ touched, setTouched ] = useState(false);
  const ref = useRef(null);

  const onChange = useCallback(function(next) {
    let kept = next;

    if (typeof clean === 'function') {
      kept = clean(next);
    }

    setValue(kept);

    if (touched === true) {
      setError(check(kept));
    }
  }, [ touched, check, clean ]);

  const onBlur = useCallback(function() {
    setTouched(true);
    setError(check(value));
  }, [ check, value ]);

  // Checks now and remembers it, so later changes are checked as they happen.
  const validate = useCallback(function() {
    const refusal = check(value);

    setTouched(true);
    setError(refusal);

    return refusal;
  }, [ check, value ]);

  // Shows a refusal the service answered with, such as a wrong code, which
  // the browser could not have known. Editing the field checks it again with
  // the local rule, which clears the message.
  const refuse = useCallback(function(sentence) {
    setTouched(true);
    setError(sentence);
  }, []);

  // Empties the field and makes it quiet again, as it was before anyone
  // touched it, such as after a form was sent and is ready for the next one.
  const reset = useCallback(function() {
    setValue('');
    setTouched(false);
    setError();
  }, []);

  return { value, error, ref, onChange, onBlur, validate, refuse, reset };
}

// Checks every field of a form at once, so every message shows together, and
// moves focus to the first one refused. Answers whether the form is valid.
export function validateAll(fields) {
  const refused = fields.filter(function(field) {
    return field.validate() != null;
  });

  if (refused.length > 0) {
    refused[ 0 ].ref.current.focus();

    return false;
  }

  return true;
}

// The check for a field that accepts anything, including nothing: a question
// the person may leave unanswered. One shared function, so a field gets the
// same check on every render.
export function anything() {
  return null;
}
